import React from "react";
import { useTranslation } from "react-i18next";
import { find, isNull, upperFirst } from "lodash";
import Text from "@/components/ui/text";
import Checkmark from "@/components/ui/checkmark";
import OptionsDropdownMenu from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import AssigneeIndicator from "@/components/controls/Task/AssigneeIndicator";
import AttachmentsIndicator from "@/components/controls/Task/AttachmentsIndicator";
import DueDateIndicator from "@/components/controls/Task/DueDateIndicator";
import HighPriorityIndicator from "@/components/controls/Task/HighPriorityIndicator";
import RemindersIndicator from "@/components/controls/Task/RemindersIndicator";
import EntitiesCell from "@/components/layouts/TasksFocus/EntitiesCell";
import { resolveTaskEntities } from "@/entities/tasks/helpers";
import { getTaskEntityNames } from "@/entities/tasks/taskLinks";
import {
  DeleteTaskModalState,
  TaskData,
  TaskModalState,
  TaskState
} from "@/entities/tasks/types";
import { UserData } from "@/entities/users/types";
import { getEnhancedRelativeTime } from "@/utils/datetime";
import { cn } from "@/utils/utils";

const TRANSLATION_KEY = "focus.tasks";

interface Props {
  task: TaskData;
  selectedTab: string;
  userId: string | undefined;
  users: UserData[];
  isCompleted: boolean;
  showStrikethrough: boolean;
  isAnimating?: boolean;
  onCheckedChange: (checked: boolean) => void;
  setTaskModalState: React.Dispatch<React.SetStateAction<TaskModalState>>;
  setDeleteTaskModalState: React.Dispatch<
    React.SetStateAction<DeleteTaskModalState>
  >;
}

const TaskCard: React.FC<Props> = ({
  task,
  selectedTab,
  userId,
  users,
  isCompleted,
  showStrikethrough,
  isAnimating = false,
  onCheckedChange,
  setTaskModalState,
  setDeleteTaskModalState
}) => {
  const { t } = useTranslation();

  const entities = resolveTaskEntities(
    getTaskEntityNames(task.entityNames, task.entities),
    task.entityIds
  );
  const assigneeData = task.assigneeId
    ? find(users, (user) => user.id === task.assigneeId)
    : undefined;
  const createdByData = task.createdBy
    ? find(users, (user) => user.id === task.createdBy)
    : undefined;
  const completedByData = task.completedBy
    ? find(users, (user) => user.id === task.completedBy)
    : undefined;
  const createdByName =
    task.createdBy === userId ? t("you") : createdByData?.displayName;

  const openEditModal = () =>
    setTaskModalState({
      editTaskId: task.id,
      editTaskData: task,
      isEditMode: true,
      isOpen: true
    });

  return (
    <div
      data-test="task-card"
      role="button"
      tabIndex={0}
      className={cn(
        "group flex flex-col w-full h-full p-4 gap-3 bg-surface-widget-card hover:bg-surface-hover-soft rounded-xl border-[0.5px] border-border-border1 transition-colors duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        isAnimating && "animate-bounce-out-down"
      )}
      onClick={openEditModal}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openEditModal();
        }
      }}
    >
      <div className="flex flex-row justify-between items-start gap-2">
        <div className="flex flex-row gap-2 items-center min-w-0">
          <div onClick={(e) => e.stopPropagation()}>
            <Checkmark
              id={`isCompleted-card-${task.id}`}
              checked={isCompleted}
              disabled={isAnimating}
              onCheckedChange={onCheckedChange}
              tooltipText={t(
                `tasks.drawer.task.${isCompleted ? "markAsOpen" : "markAsComplete"}`
              )}
            />
          </div>
          <Text
            variant="lgLabel"
            className={cn(
              "truncate",
              showStrikethrough && "line-through opacity-60"
            )}
          >
            {task.title || t("notFound")}
          </Text>
        </div>
        <div
          className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-within:opacity-100 transition-opacity duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          <OptionsDropdownMenu
            options={{
              edit: openEditModal,
              delete: () =>
                setDeleteTaskModalState({
                  isOpen: true,
                  task: { id: task.id, name: task.title }
                })
            }}
            tPrefix={`${TRANSLATION_KEY}.options`}
            buttonVariant="iconNoBg"
            iconColor="secondary"
          />
        </div>
      </div>
      {task.description && (
        <Text variant="bodySecondary" className="line-clamp-2">
          {task.description}
        </Text>
      )}
      <div className="flex flex-row flex-wrap items-center gap-3">
        <AssigneeIndicator assigneeData={assigneeData} />
        {selectedTab === TaskState.OPEN ? (
          <DueDateIndicator
            dueDate={task.dueDate}
            tooltip={t("tasks.drawer.task.tooltips.dueDate")}
            className="w-fit p-0"
          />
        ) : (
          task.completedAt && (
            <Text variant="smBodySecondary">
              {upperFirst(getEnhancedRelativeTime(t, task.completedAt))}
            </Text>
          )
        )}
        {selectedTab === TaskState.OPEN &&
          !isNull(task.reminders) &&
          (task.reminders?.items?.length ?? 0) > 0 && (
            <RemindersIndicator reminders={task.reminders} />
          )}
        {task.isHighPriority && (
          <HighPriorityIndicator
            tooltip={t("tasks.drawer.task.tooltips.highPriority")}
          />
        )}
        {!isNull(task.attachments) &&
          (task.attachments?.items?.length ?? 0) > 0 && (
            <AttachmentsIndicator attachments={task.attachments} />
          )}
        {selectedTab === TaskState.COMPLETED && completedByData && (
          <Text variant="smBodyTertiary">
            {`${t(`${TRANSLATION_KEY}.table.by`)} ${
              task.completedBy === userId
                ? t("you")
                : completedByData?.displayName
            }`}
          </Text>
        )}
        {selectedTab === TaskState.OPEN && createdByName && (
          <Text variant="smBodyTertiary">
            {`${t(`${TRANSLATION_KEY}.table.createdBy`)}: ${createdByName}`}
          </Text>
        )}
      </div>
      {entities.length > 0 && (
        <div className="flex flex-col gap-1">
          <Text variant="smBodySecondary">
            {t(`${TRANSLATION_KEY}.table.entityNames`)}
          </Text>
          <EntitiesCell entities={entities} />
        </div>
      )}
    </div>
  );
};

export default TaskCard;
